import { ChevronRight, ClipboardPlus, FileText } from "@tamagui/lucide-icons";
import Head from "expo-router/head";
import { useContext, useEffect } from "react";
import { ScrollView } from "react-native";

import {
  useGetOutboundDocuments,
  useGetPatientCarePlans,
} from "@/api/generated";
import type {
  CarePlanListItem,
  DisplayStatus,
  OutboundDocumentResponse,
} from "@/api/generated/models";
import { View, YStack } from "@/components";
import ActionCard from "@/components/ActionCard";
import ImageGallery from "@/components/ImageGallery";
import { LayoutContext } from "@/components/LayoutProvider";
import { Skeleton } from "@/components/Skeletons";
import { H2, Text } from "@/components/Typography";
import { brandName } from "@/config/brand";
import CarePlanDisplay from "@/conversation/sidebar/CarePlan";
import { useAuthStore, useImageGalleryStore } from "@/store";
import { formatDateOnly } from "@/utils/datetime";

const STATUS_LABEL: Record<DisplayStatus, string> = {
  pending: "Pending",
  in_progress: "In progress",
  delivered: "Delivered",
  failed: "Failed",
};

const STATUS_COLOR: Record<DisplayStatus, string> = {
  pending: "$textSecondary",
  in_progress: "$fillPrimary",
  delivered: "$green",
  failed: "$red",
};

type FileItem =
  | { kind: "document"; date: string; doc: OutboundDocumentResponse }
  | { kind: "carePlan"; date: string; carePlan: CarePlanListItem };

const formatDate = (iso: string) => formatDateOnly(iso.split("T")[0]);

function DocumentCard({
  doc,
  onPress,
}: {
  doc: OutboundDocumentResponse;
  onPress: () => void;
}) {
  return (
    <ActionCard
      dataTrackingId="outgoing-document-preview"
      title={doc.title}
      description={`To: ${doc.recipient_display_name} · ${formatDate(doc.created_at)}`}
      icon={<FileText color="$iconSecondary" />}
      iconAfter={
        <Text color={STATUS_COLOR[doc.display_status]} semibold size="$sm">
          {STATUS_LABEL[doc.display_status]}
        </Text>
      }
      onPress={() => doc.preview_image_urls.length > 0 && onPress()}
    />
  );
}

function CarePlanTile({
  carePlan,
  onPress,
}: {
  carePlan: CarePlanListItem;
  onPress: () => void;
}) {
  return (
    <ActionCard
      dataTrackingId="care-plan"
      extraTrackingContext={{ carePlanId: carePlan.id }}
      title={carePlan.diagnosis}
      description={`Care plan · ${formatDate(carePlan.created)}`}
      icon={<ClipboardPlus color="$iconSecondary" />}
      iconAfter={<ChevronRight color="$iconSecondary" flexShrink={0} />}
      onPress={onPress}
    />
  );
}

// The Files route: outbound documents + care plans merged into one list.
export default function Files() {
  const { isAuthInitialized } = useAuthStore();
  const { setModalContent, setShowHeader, headerHeight } =
    useContext(LayoutContext);
  const { data: documents, isLoading: documentsLoading } =
    useGetOutboundDocuments({ query: { enabled: isAuthInitialized } });
  const { data: carePlans, isLoading: carePlansLoading } =
    useGetPatientCarePlans({ query: { enabled: isAuthInitialized } });
  const { openImageGallery } = useImageGalleryStore();

  const isLoading = documentsLoading || carePlansLoading;

  useEffect(() => {
    setShowHeader(false);
    return () => {
      setShowHeader(true);
      setModalContent(null);
    };
  }, [setShowHeader, setModalContent]);

  // Open the care plan in the modal host.
  const openCarePlan = (carePlan: CarePlanListItem) => {
    setModalContent(
      () =>
        ({ onClose }: { onClose: () => void }) => (
          <CarePlanDisplay
            content={carePlan}
            senderFullNameWithTitle={carePlan.sender_full_name_with_title}
            onClose={onClose}
          />
        ),
      { dismissOnOverlayPress: true },
    );
  };

  // Merge documents + care plans into one list, newest first.
  const items: FileItem[] = [
    ...(documents ?? []).map(
      (doc): FileItem => ({ kind: "document", date: doc.created_at, doc }),
    ),
    ...(carePlans ?? []).map(
      (carePlan): FileItem => ({
        kind: "carePlan",
        date: carePlan.created,
        carePlan,
      }),
    ),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <>
      <Head>
        <title>{`Files — ${brandName}`}</title>
      </Head>
      <View
        backgroundColor="$backgroundFill"
        height="100%"
        width="100%"
        flex={1}
        borderRadius="$screenBorderRadius"
        paddingTop={headerHeight.current + 40}
        $lg={{ paddingTop: headerHeight.current }}
      >
        <ScrollView>
          <YStack
            padding="$screenPadding"
            gap="$300"
            maxWidth={960}
            alignSelf="center"
            width="100%"
          >
            <H2>Files</H2>

            {isLoading && (
              <YStack gap="$200">
                <Skeleton height={72} borderRadius="$150" />
                <Skeleton height={72} borderRadius="$150" />
              </YStack>
            )}

            {!isLoading && items.length === 0 && (
              <Text color="$textSecondary">No files yet.</Text>
            )}

            {!isLoading && items.length > 0 && (
              <YStack gap="$150">
                {items.map((item) =>
                  item.kind === "document" ? (
                    <DocumentCard
                      key={item.doc.id}
                      doc={item.doc}
                      onPress={() =>
                        item.doc.preview_image_urls.length > 0 &&
                        openImageGallery(
                          item.doc.preview_image_urls,
                          item.doc.preview_image_urls[0],
                        )
                      }
                    />
                  ) : (
                    <CarePlanTile
                      key={item.carePlan.id}
                      carePlan={item.carePlan}
                      onPress={() => openCarePlan(item.carePlan)}
                    />
                  ),
                )}
              </YStack>
            )}
          </YStack>

          <ImageGallery />
        </ScrollView>
      </View>
    </>
  );
}
